import { FunctionComponent } from 'react';
import { getGateHint, type GateHintKind } from './utils/gateHintStrings';

// Shown when init fails or the device is offline: no i18n, CDN icons or remote assets here.
const LOGO_SRC = '/update-gate-logo.png';

type Props = {
  percent: number;
  isOffline: boolean;
  hasProgress: boolean;
  hint: GateHintKind | null;
  locale: string;
  onRetry: () => void;
};

const UpdateGate: FunctionComponent<Props> = ({
  percent,
  isOffline,
  hasProgress,
  hint,
  locale,
  onRetry,
}) => {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  const shownHint = hint ?? (isOffline ? 'generic' : null);
  const hintLine = shownHint && (
    <p
      data-testid="update-gate-hint"
      role="status"
      className="text-white text-sm text-center max-w-xs"
    >
      {getGateHint(shownHint, locale)}
    </p>
  );

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen gap-6 bg-primary-darkest px-8"
      data-testid="update-gate"
    >
      <img src={LOGO_SRC} alt="" width={96} height={96} className="animate-pulse" />

      {isOffline ? (
        <>
          {/* Raw button on purpose (no Haptics, TrackedButton or SCSS); the untranslated aria-label is deliberate, i18n may have failed. */}
          <button
            type="button"
            onClick={onRetry}
            aria-label="Retry"
            data-testid="update-gate-retry"
            className="bg-white/20 rounded-full p-4"
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 1 1-3-6.7" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
          </button>
          {hintLine}
        </>
      ) : (
        <>
          <div
            role="progressbar"
            {...(hasProgress ? { 'aria-valuenow': clamped } : {})}
            aria-valuemin={0}
            aria-valuemax={100}
            data-testid="update-gate-progress"
            className="w-full max-w-xs h-3 bg-white/25 rounded-full overflow-hidden"
          >
            {/* No width transition: native progress ticks are unthrottled and would restart it before it finishes. */}
            <div
              className={
                hasProgress
                  ? 'h-full bg-white rounded-full'
                  : 'h-full w-1/3 bg-white rounded-full animate-pulse'
              }
              style={hasProgress ? { width: `${clamped}%` } : undefined}
            />
          </div>
          {hasProgress && (
            <span
              data-testid="update-gate-percent"
              className="text-white text-lg font-semibold tabular-nums"
            >
              {clamped}%
            </span>
          )}
          {hintLine}
        </>
      )}
    </div>
  );
};

export default UpdateGate;
